import type { DateTime } from "luxon";
import { useMemo } from "react";
import type { Department } from "../../../../contexts/AuthProvider";
import { useChosenDepartment } from "../../../../contexts/ChosenDepartment";
import { useDepartmentFlags } from "../../../../hooks/useDepartmentFlags";
import { useLastNameFirst } from "../../../../hooks/useNameFormat";
import { useNurseOvertimeMap } from "../../../../hooks/useNurseOvertimeMap";
import { useNurseTimeOff } from "../../../../hooks/useNurseTimeOff";
import { useRosterLookup } from "../../../../hooks/useRosterLookup";
import { getOtWeekStart, getShiftDisplayDate } from "../../../../utils/dateUtils";
import { formatPersonFullName } from "../../../../utils/formatters";
import type { Nurse, NurseData, NurseInShift, NurseOvertimeMap, ReplaceShiftRequest, SpecificPreference, WeekData } from "../../../../utils/models";
import { createDateRange, findDateIndex, generateShiftData } from "../../../../utils/nursePanelUtils";
import { getEmploymentTypeDisplayName } from "../../../../utils/utilFunctions";

export interface UseSwapDataResult {
	originNurseData: NurseData[];
	targetNurseData: NurseData[];
	originShift: NurseInShift | null;
	targetShift: NurseInShift | null;
	originShiftDate: DateTime;
	targetShiftDate: DateTime;
	originShiftType: string;
	targetShiftType: string;
}

const findShiftInWeeks = (
	weeks: WeekData[],
	shiftId: string | undefined,
): { shift: NurseInShift | null; weekIndex: number; shiftDate: DateTime; shiftType: string } | null => {
	if (!shiftId) return null;

	for (let wIdx = 0; wIdx < weeks.length; wIdx++) {
		const week = weeks[wIdx];
		for (let sIdx = 0; sIdx < week.weeklyShifts.length; sIdx++) {
			const shift = week.weeklyShifts[sIdx];
			for (let dIdx = 0; dIdx < shift.days.length; dIdx++) {
				const day = shift.days[dIdx];
				const foundShift = day.nurses.find((n: NurseInShift) => n.id === shiftId);
				if (foundShift) {
					return {
						shift: foundShift,
						weekIndex: wIdx,
						shiftDate: day.date,
						shiftType: shift.type,
					};
				}
			}
		}
	}
	return null;
};

const createNurseDataFromShift = (
	shift: NurseInShift,
	nurse: Nurse,
	timeOff: SpecificPreference[],
	weekIndex: number,
	weeks: WeekData[],
	chosenDepartment: Department,
	nurseOvertimeMap: NurseOvertimeMap | undefined,
	lastNameFirst: boolean,
): NurseData => {
	const dateRange = createDateRange(shift.startTime);
	const date = getShiftDisplayDate(shift.startTime, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes);
	const dayIdx = findDateIndex(dateRange, date);

	const shifts = generateShiftData({
		nurse,
		timeOff,
		weekIndex,
		hospitalData: { weeks },
		sourceDayIndex: dayIdx,
		targetDayIndex: dayIdx,
		chosenDepartment,
	});

	// OT data from backend
	const nurseOtDays = nurseOvertimeMap?.[nurse.id];
	const dateKey = date.toFormat("yyyy-MM-dd");
	const otInfo = nurseOtDays?.[dateKey];
	const overtimeHours = otInfo?.hours ?? 0;

	const dailyHours = shift.endTime && shift.startTime ? Math.max(0, shift.endTime.diff(shift.startTime, "hours").hours) : 0;

	// Use pre-computed cost from backend
	const predictedCostDisplay = `$${shift.cost ?? 0}`;

	return {
		id: nurse.id,
		name: formatPersonFullName(nurse, lastNameFirst),
		role: shift.role,
		predictedCost: predictedCostDisplay,
		nurseType: getEmploymentTypeDisplayName(nurse.employmentType),
		shifts: shifts,
		charge: shift.charge ?? false,
		selectable: false,
		isShiftManager: nurse.isShiftManager,
		canBeReliefCharge: nurse.canBeReliefCharge,
		overtimeHours,
		dailyHours,
		trainingStatus: shift.trainingStatus ?? nurse.trainingStatus,
		shiftTimes: {
			start: shift.startTime,
			end: shift.endTime,
		},
		...(nurse.nursingStartingDate && {
			nurseStartDate: nurse.nursingStartingDate,
		}),
		scheduledDeactivationDate: nurse.scheduledDeactivationDate ?? null,
	};
};

/**
 * The two shifts of a swap request and their nurses, found in `weeks`: the weeks around the request's date. The request
 * doesn't carry the other shift's date, so with the schedule cache on a target more than a week away isn't found and
 * the dialog shows the origin only; approving goes by the shifts' ids and still works.
 */
export const useSwapData = (
	isSwapRequest: boolean,
	swapRequest: ReplaceShiftRequest | null,
	requestDate: DateTime,
	requestShift: string,
	weeks: WeekData[],
): UseSwapDataResult => {
	const { chosenDepartment } = useChosenDepartment();
	const { timezone } = useDepartmentFlags();
	const { getNurseById } = useRosterLookup(chosenDepartment.department_id);
	const lastNameFirst = useLastNameFirst();
	const timeOffOf = useNurseTimeOff();
	// The overtime week the request's date is in, as the provider's `getWeekRange` gave it.
	const otWeekStart = getOtWeekStart(requestDate, chosenDepartment.week_start_day);
	const startDate = otWeekStart.toFormat("yyyy-MM-dd");
	const endDate = otWeekStart.plus({ days: 7 }).toFormat("yyyy-MM-dd");
	const { data: nurseOvertimeMap } = useNurseOvertimeMap(chosenDepartment.department_id, startDate, endDate, timezone, false);

	return useMemo<UseSwapDataResult>(() => {
		if (!isSwapRequest || !swapRequest) {
			return {
				originNurseData: [],
				targetNurseData: [],
				originShift: null,
				targetShift: null,
				originShiftDate: requestDate,
				targetShiftDate: requestDate,
				originShiftType: requestShift,
				targetShiftType: requestShift,
			};
		}

		const originShiftInfo = findShiftInWeeks(weeks, swapRequest.request_for_shift);
		const targetShiftInfo = findShiftInWeeks(weeks, swapRequest.request_target_shift);

		if (!originShiftInfo || !targetShiftInfo || !originShiftInfo.shift || !targetShiftInfo.shift) {
			return {
				originNurseData: [],
				targetNurseData: [],
				originShift: originShiftInfo?.shift || null,
				targetShift: targetShiftInfo?.shift || null,
				originShiftDate: originShiftInfo?.shiftDate || requestDate,
				targetShiftDate: targetShiftInfo?.shiftDate || requestDate,
				originShiftType: originShiftInfo?.shiftType || requestShift,
				targetShiftType: targetShiftInfo?.shiftType || requestShift,
			};
		}

		const originNurse = getNurseById(originShiftInfo.shift.nurseId);
		const targetNurse = getNurseById(targetShiftInfo.shift.nurseId);

		const createNurseDataArray = (shiftInfo: typeof originShiftInfo, foundNurse: Nurse | undefined) =>
			foundNurse && shiftInfo.shift
				? [
						createNurseDataFromShift(
							shiftInfo.shift,
							foundNurse,
							timeOffOf(foundNurse.id),
							shiftInfo.weekIndex,
							weeks,
							chosenDepartment,
							nurseOvertimeMap,
							lastNameFirst,
						),
					]
				: [];

		return {
			originNurseData: createNurseDataArray(originShiftInfo, originNurse),
			targetNurseData: createNurseDataArray(targetShiftInfo, targetNurse),
			originShift: originShiftInfo.shift,
			targetShift: targetShiftInfo.shift,
			originShiftDate: originShiftInfo.shiftDate,
			targetShiftDate: targetShiftInfo.shiftDate,
			originShiftType: originShiftInfo.shiftType,
			targetShiftType: targetShiftInfo.shiftType,
		};
	}, [isSwapRequest, swapRequest, weeks, getNurseById, timeOffOf, chosenDepartment, requestDate, requestShift, nurseOvertimeMap, lastNameFirst]);
};
